import type { FC, ReactNode } from 'react'
import type { TinyHouseModel } from '@/types'

import Link from 'next/link'

interface CompareTableProps {
    models: TinyHouseModel[]
}

type CompareField = 'price' | 'length' | 'width' | 'floor_area' | 'sleeps' | 'weight' | 'build_time' | 'best_for'

const ROWS: { field: CompareField, label: string }[] = [
    { field: 'price', label: "Price from" },
    { field: 'length', label: "Length" },
    { field: 'width', label: "Width" },
    { field: 'floor_area', label: "Floor area" },
    { field: 'sleeps', label: "Sleeps" },
    { field: 'weight', label: "Weight" },
    { field: 'build_time', label: "Build time" },
    { field: 'best_for', label: "Best for" }
]

export const CompareTable: FC<CompareTableProps> = ({ models }): ReactNode => {
    if (models.length === 0) return null

    return (
        <div className='overflow-x-auto rounded-2xl border border-border bg-card shadow-[0_1px_2px_rgb(0_0_0/0.04)]'>
            <table className='w-full min-w-[640px] border-collapse text-left text-sm'>
                <caption className='sr-only'>
                    Side by side comparison of our tiny house models
                </caption>

                <thead>
                    <tr className='border-b border-border'>
                        <th scope='col' className='w-44 px-6 py-5 text-xs font-semibold tracking-[0.16em] text-muted-foreground uppercase'>
                            Model
                        </th>

                        {models.map(model => (
                            <th key={model.id} scope='col' className='px-6 py-5'>
                                <Link
                                    href={`/models/${model.slug}`}
                                    className='font-heading text-2xl font-medium tracking-tight hover:text-primary'
                                >
                                    <span data-mdlfy-cms={`models:${model.id}:name`}>{model.name}</span>
                                </Link>
                            </th>
                        ))}
                    </tr>
                </thead>

                <tbody>
                    {ROWS.map(row => (
                        <tr key={row.field} className='border-b border-border last:border-b-0 even:bg-secondary/35'>
                            <th scope='row' className='px-6 py-4 font-medium text-muted-foreground'>
                                {row.label}
                            </th>

                            {models.map(model => (
                                <td key={model.id} className='px-6 py-4 font-semibold'>
                                    {model[row.field] && (
                                        <span data-mdlfy-cms={`models:${model.id}:${row.field}`}>{model[row.field]}</span>
                                    )}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    )
}
